import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Check, Leaf, Loader2, ArrowRight, User } from "lucide-react";
import { useAuth, API } from "../App";
import compressImage from "../lib/compressImage";
import { SAVI_LOGO } from "../components/PortalHeader";

const fmt = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("de-DE", { day: "numeric", month: "long" });
const inputCls = "w-full bg-white border border-stone-200 rounded-2xl px-4 py-3 text-[15px] text-[#1A1A1A] placeholder:text-[#1A1A1A]/30 focus:outline-none focus:border-[#C85A38]";

const Toggle = ({ label, on, onChange, testid }) => (
  <button
    type="button"
    onClick={() => onChange(!on)}
    className={`flex items-center justify-between w-full rounded-2xl border px-4 py-3 text-left text-[15px] transition-colors ${on ? "bg-[#8C9A7C]/15 border-[#8C9A7C] text-[#3F4A34]" : "bg-white border-stone-200 text-[#1A1A1A]/80"}`}
    data-testid={testid}
  >
    {label}
    <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${on ? "bg-[#8C9A7C] border-[#8C9A7C]" : "border-stone-300"}`}>
      {on && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
    </span>
  </button>
);

export default function RetreatOnboarding() {
  const { user, token, updateUser } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };
  const [retreat, setRetreat] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ preferred_name: "", phone: "", fun_fact: "", avatar: "" });
  const [diet, setDiet] = useState({ vegetarian: false, vegan: false, gluten_free: false, allergies: "", other_notes: "" });

  useEffect(() => {
    if (!user) return;
    if (user.guest_type !== "retreat") { navigate("/onboarding", { replace: true }); return; }
    if (user.onboarding?.final_confirmed) { navigate("/dashboard", { replace: true }); return; }
    axios.get(`${API}/retreat/me`, { headers }).then((r) => setRetreat(r.data)).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const res = await axios.post(`${API}/upload/image`, fd, { headers });
      setForm((f) => ({ ...f, avatar: res.data.url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen — bitte JPG oder PNG");
    }
  };

  const finish = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/guest/retreat-onboarding/complete`, { ...form, dietary: diet, rules_accepted: true }, { headers });
      updateUser(res.data.user);
      toast.success(`Willkommen, ${(form.preferred_name || user.name).split(" ")[0]} 🌿`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Etwas ist schiefgelaufen — bitte noch einmal versuchen");
    } finally {
      setSaving(false);
    }
  };

  const first = (user?.name || "").split(" ")[0];
  const r = retreat || user?.retreat || {};

  return (
    <div className="min-h-screen bg-[#FBF7F1] savi-bg-pattern flex flex-col" data-testid="retreat-onboarding">
      <div className="max-w-md w-full mx-auto px-5 pt-10 pb-16 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-8">
          <img src={SAVI_LOGO} alt="Savi" className="h-9 w-auto" />
          <div className="flex gap-1.5" data-testid="retreat-step-dots">
            {[0, 1, 2].map((i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[#C85A38]" : "w-1.5 bg-[#1A1A1A]/15"}`} />)}
          </div>
        </div>

        {step === 0 && (
          <div className="animate-fade-in" data-testid="retreat-step-welcome">
            <p className="font-label text-xs tracking-[0.25em] uppercase text-[#C85A38] mb-3">Euer privates Retreat</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] leading-tight mb-2">Willkommen, {first}.</h1>
            <p className="text-[#1A1A1A]/60 text-[15px] leading-relaxed mb-6">
              Die nächsten 11 Tage gehört die Villa Savi euch — <span className="font-medium text-[#1A1A1A]">{r.name}</span> mit {r.host}, {r.start && fmt(r.start)} bis {r.end && fmt(r.end)}.
            </p>
            <div className="bg-white rounded-2xl border border-stone-200 p-4 mb-5">
              <p className="text-sm text-[#1A1A1A]/70 leading-relaxed">
                Diese kleine App begleitet euch durch die Woche: das Tagesprogramm, wer sonst dabei ist, Mitfahrgelegenheiten, ein Guide für Berge und Küste. Kein Papierkram — nur drei kurze Schritte.
              </p>
            </div>
            <p className="font-label text-[11px] tracking-[0.2em] uppercase text-[#1A1A1A]/40 mb-2.5">Ein paar Hausregeln</p>
            <ul className="space-y-2 mb-8">
              {(r.notes || []).map((n, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[#1A1A1A]/75">
                  <Leaf className="w-4 h-4 text-[#8C9A7C] mt-0.5 flex-shrink-0" />{n}
                </li>
              ))}
            </ul>
            <button onClick={() => setStep(1)} className="w-full bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full py-3.5 font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition-transform" data-testid="retreat-accept-btn">
              Klingt gut, ich bin dabei <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 1 && (
          <div className="animate-fade-in" data-testid="retreat-step-you">
            <h2 className="font-serif text-3xl text-[#1A1A1A] mb-1">Ein Gesicht für die Gruppe</h2>
            <p className="text-[#1A1A1A]/55 text-sm mb-6">Damit alle wissen, neben wem sie beim Abendessen sitzen.</p>
            <div className="flex justify-center mb-6">
              <label className="relative cursor-pointer" data-testid="retreat-avatar-label">
                <div className={`w-28 h-28 rounded-full overflow-hidden bg-white border-4 shadow-lg ${form.avatar ? "border-white" : "border-[#C85A38] ring-4 ring-[#C85A38]/15"}`}>
                  {form.avatar ? <img src={form.avatar} alt="You" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="w-12 h-12 text-[#1A1A1A]/25" /></div>}
                </div>
                <span className="absolute bottom-0 right-0 w-10 h-10 bg-[#C85A38] rounded-full flex items-center justify-center shadow-lg"><Camera className="w-5 h-5 text-white" /></span>
                <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="retreat-avatar-input" />
              </label>
            </div>
            <div className="space-y-3">
              <input value={form.preferred_name} onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} placeholder={`Wie sollen wir dich nennen? (${first})`} className={inputCls} data-testid="retreat-preferred-name" />
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="WhatsApp-Nummer (für die Gruppe)" className={inputCls} data-testid="retreat-phone" />
              <textarea value={form.fun_fact} onChange={(e) => setForm({ ...form, fun_fact: e.target.value })} placeholder="Ein Fun Fact über dich — der perfekte Gesprächsöffner" rows={3} className={`${inputCls} resize-none`} data-testid="retreat-fun-fact" />
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setStep(0)} className="px-5 py-3.5 rounded-full border border-stone-300 text-[#1A1A1A]/70 text-sm" data-testid="retreat-back-btn">Zurück</button>
              <button onClick={() => setStep(2)} className="flex-1 bg-[#C85A38] hover:bg-[#A84830] text-white rounded-full py-3.5 font-medium flex items-center justify-center gap-2" data-testid="retreat-next-btn">
                Weiter <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-in" data-testid="retreat-step-food">
            <h2 className="font-serif text-3xl text-[#1A1A1A] mb-1">Was sollte die Küche wissen?</h2>
            <p className="text-[#1A1A1A]/55 text-sm mb-6">Steve kocht für die ganze Gruppe. Vor allem Allergien — bitte nicht schüchtern sein.</p>
            <div className="space-y-2.5 mb-3">
              <Toggle label="Vegetarisch" on={diet.vegetarian} onChange={(v) => setDiet({ ...diet, vegetarian: v })} testid="retreat-diet-vegetarian" />
              <Toggle label="Vegan" on={diet.vegan} onChange={(v) => setDiet({ ...diet, vegan: v })} testid="retreat-diet-vegan" />
              <Toggle label="Glutenfrei" on={diet.gluten_free} onChange={(v) => setDiet({ ...diet, gluten_free: v })} testid="retreat-diet-gluten" />
            </div>
            <div className="space-y-3">
              <input value={diet.allergies} onChange={(e) => setDiet({ ...diet, allergies: e.target.value })} placeholder="Allergien oder Intoleranzen (Nüsse, Laktose, …)" className={inputCls} data-testid="retreat-allergies" />
              <input value={diet.other_notes} onChange={(e) => setDiet({ ...diet, other_notes: e.target.value })} placeholder="Sonst noch was — Abneigungen, Fastenzeiten, Kaffeebestellung" className={inputCls} data-testid="retreat-diet-notes" />
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setStep(1)} className="px-5 py-3.5 rounded-full border border-stone-300 text-[#1A1A1A]/70 text-sm" data-testid="retreat-back-btn-2">Zurück</button>
              <button onClick={finish} disabled={saving} className="flex-1 bg-[#C85A38] hover:bg-[#A84830] disabled:opacity-60 text-white rounded-full py-3.5 font-medium flex items-center justify-center gap-2" data-testid="retreat-finish-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Zum Programm
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
